import { useMemo, useState } from "react";
import { Breadcrumb, Button, Space, Table, Tag, Tree, Typography } from "antd";
import type { TreeDataNode } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarsOutlined,
  ClearOutlined,
  EnvironmentOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import type { GeoJsonFeature, GravePositionProperties } from "./gisApi";
import {
  gisPositionStateLabel,
  gisPositionStateTone,
  type GisIdentityStatus,
  type GisPositionState,
} from "./gisVocabulary";

function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

type ParsedPosition = {
  zone: string;
  zoneLabel: string;
  plot?: string | undefined;
  plotLabel?: string | undefined;
  row?: string | undefined;
  rowLabel?: string | undefined;
  graveNumber: string;
};

/**
 * Phân tách mã vị trí mộ (hoặc sectionCode/sectionName) thành các cấp cây: Khu -> Lô -> Hàng -> Mộ.
 * Hỗ trợ cả chuẩn mới S12.4 (`K1·L1·H1·01`), chuẩn cũ S11 (`A-01-003`), và phân khu tự do.
 */
function parsePosition(code: string | undefined, props: GravePositionProperties): ParsedPosition {
  const cleanCode = (code || "").trim();

  // 1. Chuẩn S12.4: phân cách bằng dấu chấm giữa `·` (U+00B7)
  if (cleanCode.includes("·")) {
    const parts = cleanCode.split("·").map((p) => p.trim());
    const [p0 = "", p1 = "", p2 = "", p3 = ""] = parts;
    if (parts.length >= 4) {
      return {
        zone: p0,
        zoneLabel: `Khu ${p0}`,
        plot: p1,
        plotLabel: `Lô ${p1}`,
        row: p2,
        rowLabel: `Hàng ${p2}`,
        graveNumber: p3,
      };
    }
    if (parts.length === 3) {
      return {
        zone: p0,
        zoneLabel: `Khu ${p0}`,
        row: p1,
        rowLabel: `Hàng ${p1}`,
        graveNumber: p2,
      };
    }
    if (parts.length === 2) {
      return {
        zone: p0,
        zoneLabel: `Khu ${p0}`,
        graveNumber: p1,
      };
    }
  }

  // 2. Chuẩn cũ S11: phân cách bằng dấu gạch ngang `-`
  if (cleanCode.includes("-")) {
    const parts = cleanCode.split("-").map((p) => p.trim());
    const [p0 = "", p1 = "", p2 = "", p3 = ""] = parts;
    if (parts.length >= 4) {
      return {
        zone: p0,
        zoneLabel: `Khu ${p0}`,
        plot: p1,
        plotLabel: `Lô ${p1}`,
        row: p2,
        rowLabel: `Hàng ${p2}`,
        graveNumber: p3,
      };
    }
    if (parts.length === 3) {
      // Dạng A-01-003: Hàng A - Lô 01 - Mộ 003
      const zoneName = props.sectionName || (props.sectionCode ? `Khu ${props.sectionCode}` : "Khu chính");
      return {
        zone: props.sectionCode || "KHU_CHINH",
        zoneLabel: zoneName,
        plot: p1,
        plotLabel: `Lô ${p1}`,
        row: p0,
        rowLabel: `Hàng ${p0}`,
        graveNumber: p2,
      };
    }
    if (parts.length === 2) {
      return {
        zone: p0,
        zoneLabel: `Khu ${p0}`,
        graveNumber: p1,
      };
    }
  }

  // 3. Fallback: Dùng sectionCode / sectionName từ properties
  const zoneKey = props.sectionCode || props.sectionName || "CHUA_PHAN_KHU";
  const zoneLabel = props.sectionName || (props.sectionCode ? `Khu ${props.sectionCode}` : "Chưa phân khu");
  return {
    zone: zoneKey,
    zoneLabel,
    graveNumber: cleanCode || "—",
  };
}

export type GraveTableRow = {
  readonly id: string;
  readonly key: string;
  readonly positionCode: string;
  readonly sectionDisplay: string;
  readonly positionState?: GisPositionState | undefined;
  readonly identityStatus?: GisIdentityStatus | undefined;
  readonly martyrName?: string | undefined;
  readonly precisionMeters?: number | undefined;
  readonly rawFeature: GeoJsonFeature;
  readonly zone: string;
  readonly plot?: string | undefined;
  readonly row?: string | undefined;
};

export type GisTableViewProps = {
  readonly graves: GeoJsonFeature[];
  readonly selectedFeatureId: string | null;
  readonly onSelectFeature: (id: string) => void;
  readonly search?: string;
};

export function GisTableView({
  graves,
  selectedFeatureId,
  onSelectFeature,
}: GisTableViewProps) {
  const [selectedTreeKey, setSelectedTreeKey] = useState<string>("root");
  const [pageSize, setPageSize] = useState<number>(10);

  // Chuẩn hoá toàn bộ mộ thành danh sách GraveTableRow
  const allRows = useMemo<GraveTableRow[]>(() => {
    return graves.map((feature) => {
      const props = (feature.properties || {}) as GravePositionProperties;
      const pos = parsePosition(props.positionCode, props);
      const positionCode = props.positionCode || String(feature.id ?? "—");
      const sectionDisplay = [pos.zoneLabel, pos.plotLabel, pos.rowLabel]
        .filter(Boolean)
        .join(" / ");

      return {
        id: String(feature.id ?? positionCode),
        key: String(feature.id ?? positionCode),
        positionCode,
        sectionDisplay,
        positionState: props.positionState,
        identityStatus: props.identityStatus,
        martyrName: props.martyrName,
        precisionMeters: props.precisionMeters,
        rawFeature: feature,
        zone: pos.zone,
        plot: pos.plot,
        row: pos.row,
      };
    });
  }, [graves]);

  // Xây dựng cây phân cấp (TreeDataNode) từ allRows
  const { treeData, defaultExpandedKeys } = useMemo(() => {
    type RowData = { key: string; row: string; label: string; count: number };
    type PlotData = { key: string; plot: string; label: string; count: number; rows: Map<string, RowData> };
    type ZoneData = {
      key: string;
      zone: string;
      label: string;
      count: number;
      plots: Map<string, PlotData>;
      directRows: Map<string, RowData>;
    };

    const zones = new Map<string, ZoneData>();

    for (const item of allRows) {
      let zoneNode = zones.get(item.zone);
      if (!zoneNode) {
        zoneNode = {
          key: `zone-${item.zone}`,
          zone: item.zone,
          label: item.zone === "CHUA_PHAN_KHU" ? "Chưa phân khu" : `Khu ${item.zone}`,
          count: 0,
          plots: new Map(),
          directRows: new Map(),
        };
        zones.set(item.zone, zoneNode);
      }
      zoneNode.count += 1;

      if (item.plot) {
        let plotNode = zoneNode.plots.get(item.plot);
        if (!plotNode) {
          plotNode = {
            key: `plot-${item.zone}-${item.plot}`,
            plot: item.plot,
            label: `Lô ${item.plot}`,
            count: 0,
            rows: new Map(),
          };
          zoneNode.plots.set(item.plot, plotNode);
        }
        plotNode.count += 1;

        if (item.row) {
          let rowNode = plotNode.rows.get(item.row);
          if (!rowNode) {
            rowNode = {
              key: `row-${item.zone}-${item.plot}-${item.row}`,
              row: item.row,
              label: `Hàng ${item.row}`,
              count: 0,
            };
            plotNode.rows.set(item.row, rowNode);
          }
          rowNode.count += 1;
        }
      } else if (item.row) {
        let rowNode = zoneNode.directRows.get(item.row);
        if (!rowNode) {
          rowNode = {
            key: `row-${item.zone}-${item.row}`,
            row: item.row,
            label: `Hàng ${item.row}`,
            count: 0,
          };
          zoneNode.directRows.set(item.row, rowNode);
        }
        rowNode.count += 1;
      }
    }

    const expandedKeys: string[] = ["root"];

    const sortedZones = Array.from(zones.values()).sort((a, b) =>
      naturalCompare(a.label, b.label)
    );

    const zoneNodes: TreeDataNode[] = sortedZones.map((z) => {
      expandedKeys.push(z.key);

      const plotNodes: TreeDataNode[] = Array.from(z.plots.values())
        .sort((a, b) => naturalCompare(a.label, b.label))
        .map((p) => {
          const rowNodes: TreeDataNode[] = Array.from(p.rows.values())
            .sort((a, b) => naturalCompare(a.label, b.label))
            .map((r) => ({
              key: r.key,
              icon: <BarsOutlined style={{ color: "var(--ncc-color-text-secondary)" }} />,
              title: (
                <span style={{ fontSize: 12 }}>
                  {r.label}{" "}
                  <Tag style={{ fontSize: 10, padding: "0 4px", lineHeight: "16px", marginLeft: 4 }}>
                    {r.count}
                  </Tag>
                </span>
              ),
            }));

          return {
            key: p.key,
            icon: <AppstoreOutlined style={{ color: "var(--ncc-color-action-primary)" }} />,
            title: (
              <span style={{ fontSize: 12 }}>
                {p.label}{" "}
                <Tag style={{ fontSize: 10, padding: "0 4px", lineHeight: "16px", marginLeft: 4 }}>
                  {p.count}
                </Tag>
              </span>
            ),
            ...(rowNodes.length > 0 ? { children: rowNodes } : {}),
          };
        });

      const directRowNodes: TreeDataNode[] = Array.from(z.directRows.values())
        .sort((a, b) => naturalCompare(a.label, b.label))
        .map((r) => ({
          key: r.key,
          icon: <BarsOutlined style={{ color: "var(--ncc-color-text-secondary)" }} />,
          title: (
            <span style={{ fontSize: 12 }}>
              {r.label}{" "}
              <Tag style={{ fontSize: 10, padding: "0 4px", lineHeight: "16px", marginLeft: 4 }}>
                {r.count}
              </Tag>
            </span>
          ),
        }));

      const allChildren = [...plotNodes, ...directRowNodes];

      return {
        key: z.key,
        icon: <FolderOutlined style={{ color: "var(--ncc-color-action-primary)" }} />,
        title: (
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            {z.label}{" "}
            <Tag color="processing" style={{ fontSize: 10, padding: "0 4px", lineHeight: "16px", marginLeft: 4 }}>
              {z.count}
            </Tag>
          </span>
        ),
        ...(allChildren.length > 0 ? { children: allChildren } : {}),
      };
    });

    const rootNode: TreeDataNode = {
      key: "root",
      icon: <ApartmentOutlined style={{ color: "var(--ncc-color-action-primary)", fontSize: 14 }} />,
      title: (
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          Tất cả khu vực{" "}
          <Tag color="blue" style={{ fontSize: 10, padding: "0 4px", lineHeight: "16px", marginLeft: 4 }}>
            {allRows.length}
          </Tag>
        </span>
      ),
      children: zoneNodes,
    };

    return {
      treeData: [rootNode],
      defaultExpandedKeys: expandedKeys,
    };
  }, [allRows]);

  // Lọc các mộ theo node đang chọn trên cây
  const filteredByTree = useMemo<GraveTableRow[]>(() => {
    if (selectedTreeKey === "root") {
      return allRows;
    }
    if (selectedTreeKey.startsWith("zone-")) {
      const zone = selectedTreeKey.slice(5);
      return allRows.filter((r) => r.zone === zone);
    }
    if (selectedTreeKey.startsWith("plot-")) {
      const parts = selectedTreeKey.split("-");
      const zone = parts[1];
      const plot = parts[2];
      return allRows.filter((r) => r.zone === zone && r.plot === plot);
    }
    if (selectedTreeKey.startsWith("row-")) {
      const parts = selectedTreeKey.split("-");
      if (parts.length >= 4) {
        const zone = parts[1];
        const plot = parts[2];
        const row = parts[3];
        return allRows.filter((r) => r.zone === zone && r.plot === plot && r.row === row);
      }
      if (parts.length === 3) {
        const zone = parts[1];
        const row = parts[2];
        return allRows.filter((r) => r.zone === zone && r.row === row);
      }
    }
    return allRows;
  }, [allRows, selectedTreeKey]);

  // Nhãn tiêu đề hiển thị breadcrumb theo node cây
  const pathLabel = useMemo<string>(() => {
    if (selectedTreeKey === "root") return "Tất cả khu vực";
    if (selectedTreeKey.startsWith("zone-")) {
      const zone = selectedTreeKey.slice(5);
      return `Khu ${zone}`;
    }
    if (selectedTreeKey.startsWith("plot-")) {
      const parts = selectedTreeKey.split("-");
      return `Khu ${parts[1]} / Lô ${parts[2]}`;
    }
    if (selectedTreeKey.startsWith("row-")) {
      const parts = selectedTreeKey.split("-");
      if (parts.length >= 4) {
        return `Khu ${parts[1]} / Lô ${parts[2]} / Hàng ${parts[3]}`;
      }
      return `Khu ${parts[1]} / Hàng ${parts[2]}`;
    }
    return "Tất cả khu vực";
  }, [selectedTreeKey]);

  const columns: ColumnsType<GraveTableRow> = [
    {
      title: "Mã vị trí",
      dataIndex: "positionCode",
      key: "positionCode",
      width: 160,
      render: (_text, record) => {
        const isSelected = record.rawFeature.id === selectedFeatureId;
        return (
          <Space orientation="horizontal" size={6}>
            <EnvironmentOutlined
              style={{ color: isSelected ? "var(--ncc-color-action-primary)" : "var(--ncc-color-text-secondary)" }}
              aria-hidden="true"
            />
            <Typography.Text
              code
              style={{
                fontWeight: 600,
                color: isSelected ? "var(--ncc-color-action-primary)" : "var(--ncc-color-action-primary)",
              }}
            >
              {record.positionCode}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "Khu / Lô / Hàng",
      dataIndex: "sectionDisplay",
      key: "sectionDisplay",
      width: 170,
      render: (_text, record) => (
        <Typography.Text style={{ fontSize: 12 }}>
          {record.sectionDisplay || "—"}
        </Typography.Text>
      ),
    },
    {
      title: "Trạng thái an táng",
      dataIndex: "positionState",
      key: "positionState",
      width: 160,
      render: (_text, record) => {
        const label = gisPositionStateLabel(record.positionState);
        const tone = gisPositionStateTone(record.positionState);
        const tagColor =
          tone === "success" ? "green" : tone === "danger" ? "red" : tone === "info" ? "blue" : "default";
        return <Tag color={tagColor}>{label}</Tag>;
      },
    },
    {
      title: "Thông tin liệt sĩ",
      dataIndex: "martyrName",
      key: "martyrName",
      render: (_text, record) =>
        record.martyrName ? (
          <Typography.Text strong style={{ fontSize: 12 }}>
            {record.martyrName}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: "Sai số",
      dataIndex: "precisionMeters",
      key: "precisionMeters",
      width: 100,
      render: (_text, record) =>
        record.precisionMeters != null ? (
          <Typography.Text style={{ fontSize: 12 }}>± {record.precisionMeters} m</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 90,
      render: (_text, record) => {
        const isSelected = record.rawFeature.id === selectedFeatureId;
        return (
          <Button
            type={isSelected ? "primary" : "link"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (record.rawFeature.id) onSelectFeature(record.rawFeature.id);
            }}
            aria-label={`Chọn ${record.positionCode}`}
          >
            {isSelected ? "Đang chọn" : "Chọn"}
          </Button>
        );
      },
    },
  ];

  return (
    <div
      className="gis-tree-table-layout"
      style={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        background: "var(--ncc-color-bg-surface)",
      }}
    >
      {/* 1. Cột bên trái: CÂY PHÂN CẤP (Khu / Lô / Hàng) */}
      <div
        className="gis-tree-sidebar"
        style={{
          width: 220,
          minWidth: 180,
          borderRight: "1px solid var(--ncc-color-border-default)",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          background: "var(--ncc-color-bg-subtle)",
        }}
      >
        <div
          style={{
            padding: "10px 14px",
            borderBottom: "1px solid var(--ncc-color-border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography.Text strong style={{ fontSize: 13, color: "var(--ncc-color-text-emphasis)" }}>
            Cấu trúc phân khu
          </Typography.Text>
          {selectedTreeKey !== "root" && (
            <Button
              size="small"
              type="text"
              icon={<ClearOutlined />}
              onClick={() => setSelectedTreeKey("root")}
              title="Xem tất cả mộ"
            >
              Tất cả
            </Button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 6px" }}>
          <Tree
            showIcon
            treeData={treeData}
            defaultExpandedKeys={defaultExpandedKeys}
            selectedKeys={[selectedTreeKey]}
            onSelect={(keys) => {
              const key = keys[0];
              if (key != null) setSelectedTreeKey(String(key));
            }}
          />
        </div>
      </div>

      {/* 2. Cột bên phải: BẢNG DANH SÁCH MỘ (Có phân trang) */}
      <div
        className="gis-table-content"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Header trên của bảng */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 16px",
            borderBottom: "1px solid var(--ncc-color-border-default)",
            background: "var(--ncc-color-bg-surface)",
            flexShrink: 0,
          }}
        >
          <Breadcrumb
            items={[
              {
                title: (
                  <span
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer", color: selectedTreeKey === "root" ? "var(--ncc-color-text-emphasis)" : "var(--ncc-color-action-primary)" }}
                    onClick={() => setSelectedTreeKey("root")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelectedTreeKey("root");
                    }}
                  >
                    Nghĩa trang
                  </span>
                ),
              },
              ...(selectedTreeKey !== "root" ? [{ title: pathLabel }] : []),
            ]}
          />

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Hiển thị <strong>{filteredByTree.length}</strong> vị trí mộ
          </Typography.Text>
        </div>

        {/* Ant Design Table với phân trang chuẩn */}
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          <Table<GraveTableRow>
            rowKey="id"
            columns={columns}
            dataSource={filteredByTree}
            size="small"
            scroll={{ x: 550 }}
            pagination={{
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `Hiển thị ${range[0]}–${range[1]} / ${total} vị trí mộ`,
              onChange: (_page, size) => {
                if (size && size !== pageSize) setPageSize(size);
              },
            }}
            onRow={(record) => {
              const isSelected = record.rawFeature.id === selectedFeatureId;
              return {
                className: `gis-grave-row ${isSelected ? "gis-grave-row--selected" : ""}`,
                style: {
                  cursor: "pointer",
                  background: isSelected ? "rgba(22, 119, 255, 0.08)" : undefined,
                },
                onClick: () => {
                  if (record.rawFeature.id) onSelectFeature(record.rawFeature.id);
                },
              };
            }}
          />
        </div>
      </div>
    </div>
  );
}
